import { useEffect, useRef } from 'react';
import type { calculateSpritePositions } from 'src/utils/image';

interface UseAnimationProps {
  isReady: boolean;
  coordinates: ReturnType<typeof calculateSpritePositions>;
  frameCount: number;
  duration: number;
  width: number;
  height: number;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  spriteImageRef: React.RefObject<HTMLImageElement | null>;
}

/**
 * [애니메이션 훅]
 *
 * @param props
 * ```ts
 * {
 *   isReady: boolean, // 준비 상태
 *   coordinates: ReturnType<typeof calculateSpritePositions>, // 스프라이트 좌표
 *   frameCount: number, // 프레임 수
 *   duration: number, // 애니메이션 지속 시간
 *   width: number, // 캔버스 너비
 *   height: number, // 캔버스 높이
 *   canvasRef: React.RefObject<HTMLCanvasElement>, // 캔버스 요소 참조
 *   spriteImageRef: React.RefObject<HTMLImageElement>, // 스프라이트 이미지 요소 참조
 * }
 * ```
 */
export const useAnimation = ({
  isReady,
  coordinates,
  frameCount,
  duration,
  width,
  height,
  canvasRef,
  spriteImageRef,
}: UseAnimationProps) => {
  // 애니메이션 관련 refs
  const frameRef = useRef<number>(undefined); // requestAnimationFrame의 ID
  const lastFrameTimeRef = useRef(0); // 마지막 프레임 시간
  const currentFrameRef = useRef(0); // 현재 프레임 인덱스

  /**
   * 스프라이트 애니메이션 처리
   */
  useEffect(() => {
    if (!isReady || !canvasRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx || !spriteImageRef.current) return;

    // 프레임 간격 계산 (밀리초)
    const frameInterval = (duration * 1000) / frameCount;

    const animate = (timestamp: number) => {
      // 첫 프레임 시작 시간 설정
      if (!lastFrameTimeRef.current) {
        lastFrameTimeRef.current = timestamp;
      }

      const elapsed = timestamp - lastFrameTimeRef.current;

      // 프레임 간격이 지났으면 다음 프레임 그리기
      if (elapsed >= frameInterval) {
        // Canvas 초기화
        ctx.clearRect(0, 0, width, height);
        // 현재 프레임의 스프라이트 좌표 가져오기
        const { x, y } = coordinates[currentFrameRef.current];
        const image = spriteImageRef.current;

        if (image) {
          // 스프라이트 시트에서 현재 프레임 영역을 Canvas에 그리기
          ctx.drawImage(image, x, y, width, height, 0, 0, width, height);
        }

        // 다음 프레임으로 이동
        currentFrameRef.current = (currentFrameRef.current + 1) % frameCount;
        lastFrameTimeRef.current = timestamp;
      }

      // 다음 프레임 요청
      frameRef.current = requestAnimationFrame(animate);
    };

    // 애니메이션 시작
    frameRef.current = requestAnimationFrame(animate);

    // cleanup: 애니메이션 중지
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [isReady, coordinates, width, height, frameCount, duration]);
};
